import { useCallback, useLayoutEffect, useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { getCategories } from '../common/category'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading, notifyMessage } from '../common/store/slices/application'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { yesNo } from '../common/constant/classification'
import { memberUrl } from '../common/constant/appUrl'
import { tabValue } from '../member/memberService'
import { translate } from '../../i18n'
import { toApiYmd } from '../../utils/dateUtil'
import { GetCategoryMstDto, Option } from '../../dataAccess/webApi/dto/categoryMstDto'
import { GetChildrenDiseaseTestDto } from '../../dataAccess/webApi/dto/childrenDiseaseTestDto'
import {
  executeGetChildrenDiseaseTest,
  executePutChildrenDiseaseTest
} from '../../dataAccess/webApi/dao/childrenDiseaseTestDao'

/** カテゴリーキー */
const CATEGORY_KEY = 'diseaseTest'
/** サブカテゴリーキー */
const SUB_CATEGORIES = [
  'diseaseTest.testName',
  'diseaseTest.testDate',
  'diseaseTest.testResult'
]
/** 市民用事業ID（市民用の項目は事業ID'0'） */
const DEFAULT_PROJECT_ID = '0'
/** 市民用施設ID（市民用のデータは施設ID'0'で作成する） */
const DEFAULT_FACILITY_ID = '0'

/** URLパラメータ */
interface UrlParams {
  childId: string
}

/** 検査情報の入力フォームの項目の定義 */
export interface Inputs {
  diseaseTest?: DiseaseTestForm[]
}[]

/** 入力フォームの項目の定義 */
export interface DiseaseTestForm {
  seq?: number | null
  code: string
  testName?: string | null
  testDate?: Date | null
  testResult?: string | null
  deleted?: boolean
}

/** 入力フォームの初期値 */
const FORM_DEFAULT_VALUES: Inputs = {
  diseaseTest: [],
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const history = useHistory()
  const dispatch = useDispatch()

  // URLパラメータ取得
  const { childId } = useParams<UrlParams>()
  // カテゴリーマスタ
  const [categoryMst, setCategoryMst] = useState<GetCategoryMstDto[]>()
  // リザルトコード
  const [childrenResultCode, setChildrenResultCode] = useState<number>()
  // オペレーションログ
  const { addOperationLog } = useOperationLog()

  // 入力フォーム初期値設定
  const formMethods = useForm<Inputs>({
    defaultValues: FORM_DEFAULT_VALUES,
  })
  const diseaseTests = useWatch({ name: CATEGORY_KEY, control: formMethods.control })
  const { fields, append } = useFieldArray({
    control: formMethods.control,
    name: CATEGORY_KEY
  })

  // 初期処理（ちらつき防止のためにuseLayoutEffect使用）
  useLayoutEffect(() => {
    addOperationLog({
      operationId: OperationId.OP_00000001,
      accessData: [{ userIdRegFlag: yesNo.yes, childId }]
    })
    
    dispatch(
      showLoading(
        errorHandle(async () => {
          const [
            category, 
            // child,
            diseaseTestResp
          ] = await Promise.all([
            // カテゴリーマスタ取得
            getCategories({projectId: DEFAULT_PROJECT_ID, categories: [CATEGORY_KEY, ...SUB_CATEGORIES]}),
            // 検査情報情報取得
            executeGetChildrenDiseaseTest(childId, DEFAULT_FACILITY_ID)
          ])

          // カテゴリーマスタ設定
          setCategoryMst(category)
          // 入力フォームに設定
          formMethods.reset({
            ...formMethods.getValues(),
            ...getInitialInputs(diseaseTestResp.result),
          })
          
          const diseaseTestCategory = category?.find((value) => value.category === CATEGORY_KEY)
          // diseaseTestCategory.options が変更されたときに、対応する testName を設定
          diseaseTestCategory?.options?.forEach((opt: Option, index: number) => {
            formMethods.setValue(`${CATEGORY_KEY}.${index}.testName`, opt.optionName);
            formMethods.setValue(`${CATEGORY_KEY}.${index}.code`, opt.code);
          });
          
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 確定処理
  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({ operationId: OperationId.OP_00000037 })

      dispatch(
        showLoading({
          process: errorHandle(async () => {
            // 登録情報を設定
            const regData = getRegistDiseaseTest(data)
            // 登録実行
            const resp = await executePutChildrenDiseaseTest(childId, regData)
            if (resp.resultCode) {
              // 失敗時
              setChildrenResultCode(resp.resultCode)
            } else {
              // 成功時
              dispatch(notifyMessage(translate('memberModifyChildDiseaseTest.success.completionMessage')))
              history.push(memberUrl.url(), { tabValue: tabValue.child, childId })
            }
          }),
          isHiddenMain: false,
        })
      )
    },
    [addOperationLog]
  )

  const onFieldRemove = (i: number) => {
    formMethods.setValue(`${CATEGORY_KEY}.${i}.deleted`, true)
  }
  
  const onFieldAppend = () => {append({})}
  
  // 情報返却
  return {
    CATEGORY_KEY,
    SUB_CATEGORIES,
    formMethods,
    fields,
    diseaseTests,
    categoryMst,
    onFieldAppend,
    onFieldRemove,
    onSubmit,
    childrenResultCode,
  }
}

/**
 * 入力フォームの初期値設定
 */
const getInitialInputs = (childrenDiseaseTest: GetChildrenDiseaseTestDto[]) => {
  const inputs = {
    diseaseTest: convertDiseaseTest(childrenDiseaseTest)
  }

  return inputs
}

/**
 * 入力フォームのtestDateがDate型のためtestDateをDate型へ変換
 */
const convertDiseaseTest = (diseaseTest: GetChildrenDiseaseTestDto[]): DiseaseTestForm[] => {
  const diseaseTestForm: DiseaseTestForm[] = []
  diseaseTest.map((value) => {
    diseaseTestForm.push({
      ...value,
      testDate: value.testDate ? new Date(value.testDate) : null,
    })
  })
  return diseaseTestForm
}

/**
 * お子さま情報登録APIのINPUT用の検査情報を取得
 * @param data 入力値
 * @returns お子さま情報登録APIのINPUT用の検査情報
 */
export const getRegistDiseaseTest = (data: Inputs) => {
  
  const base = {
    facilityId: DEFAULT_FACILITY_ID,
  }
  
  if (data.diseaseTest == null || data.diseaseTest.length === 0) return {...base, diseaseTest: []}
  
  const targetData = data.diseaseTest.filter(e => !e.deleted && (e.testName?.trim() || e.testDate != null || e.testResult != null ))
  
  const diseaseTest = targetData.map((item, i) =>{
    return {
      facilityId: DEFAULT_FACILITY_ID,
      seq: i + 1,
      code: item.code ?? '99',
      testName: item.testName,
      testDate: item.testDate ? toApiYmd(item.testDate) : null,
      testResult: item.testResult
    }
  })
  
  return {...base, diseaseTest}
}